'use client';

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useWallet } from '@/hooks/use-wallet';

const KEY_PREFIX = 'chat-last-seen-id';

function storageKey(address: string) {
  return `${KEY_PREFIX}:${address.toLowerCase()}`;
}

function getLastSeenFor(address: string | undefined): number {
  if (typeof window === 'undefined' || !address) return 0;
  return parseInt(localStorage.getItem(storageKey(address)) || '0', 10);
}

function subscribe(cb: () => void) {
  window.addEventListener('storage', cb);
  return () => window.removeEventListener('storage', cb);
}

export function markChatSeen(latestId: number, address: string) {
  const key = storageKey(address);
  const current = parseInt(localStorage.getItem(key) || '0', 10);
  if (latestId > current) {
    localStorage.setItem(key, String(latestId));
    window.dispatchEvent(new StorageEvent('storage', { key }));
  }
}

/** Chat unread surface badge. `'new'` was previously returned for
 *  not-yet-joined viewers, but the user removed the "NEW" pill from
 *  every consumer (chip rail, desktop chat sheet) because it doubled
 *  as marketing pressure rather than signal — keeping the type narrow
 *  to `'dot' | null` ensures stale renderers can't accidentally
 *  resurrect the label. The dot still reflects "you have unread
 *  messages." */
export type ChatBadge = 'dot' | null;

export function useChatUnread() {
  const { address, isConnected } = useWallet();

  const getSnapshot = useMemo(
    () => () => getLastSeenFor(address ?? undefined),
    [address]
  );
  const lastSeen = useSyncExternalStore(subscribe, getSnapshot, () => 0);
  const hasJoined = isConnected && lastSeen > 0;

  const { data: latestId } = useQuery({
    queryKey: ['chat-latest-id', address?.toLowerCase() ?? null],
    queryFn: async () => {
      const res = await fetch('/api/chat?limit=1');
      if (!res.ok) return 0;
      const json = await res.json();
      const msgs = json.messages as { id: number }[];
      return msgs[0]?.id ?? 0;
    },
    staleTime: 30_000,
    refetchInterval: hasJoined ? 30_000 : false,
    enabled: isConnected,
  });

  const markSeen = useCallback(() => {
    if (latestId && latestId > 0 && address) markChatSeen(latestId, address);
  }, [latestId, address]);

  // Drop the 'new' state entirely (was shown for not-yet-joined
  // viewers as a "Click here!" pill). The user wanted the badge to
  // reflect signal only — actual unread messages — so it's `'dot'`
  // when fresher than `lastSeen` and `null` otherwise.
  const badge: ChatBadge =
    isConnected && hasJoined && (latestId ?? 0) > lastSeen ? 'dot' : null;

  return { badge, markSeen };
}
